import React from 'react'
import { type FormikContextType, useFormikContext } from 'formik'
import { type AssetHoldingProjectIdentifier } from '../../../api-schema-types/asset-holdings/CellOwnerAgreementPayloadDtos'
import { type AssetProjectUpdateDto } from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import {
  useCellOwnerAgreementsApiCreateMutation,
  useCellOwnerAgreementsApiCreatePdfMutation
} from '../../../api-services/asset-holdings/cellOwnerAgreementApi'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'
import ButtonLoadingComponent from '../../../views/components/shared/ButtonLoadingComponent'
import { FormikFieldErrors } from '../../components/ui-widgets/FormikFieldErrors'
import FormikTextField from '../../components/ui-widgets/FormikTextField'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import { type EditSolarProjectComponentProps } from './EditSolarProjectForm'
import { Grid, Button, Typography, FormControl, InputLabel, MenuItem, FormHelperText, Select } from '@mui/material'

const EditCellOwnerAgreementsPanel: React.FC<EditSolarProjectComponentProps> = ({
  project,
  overrideLockedFields,
  triggerRefetch
}: EditSolarProjectComponentProps) => {
  const dispatch = useAppDispatch()

  const formikProps: FormikContextType<AssetProjectUpdateDto> = useFormikContext()
  const formikErrors = new FormikFieldErrors(formikProps)

  const isFieldLocked = (): boolean => {
    return !overrideLockedFields && project.cellOwnerAgreements.isLocked
  }

  // ==================================================================================================================
  // Generate COAs
  // ==================================================================================================================

  const [apiCreateCOA, { isLoading: createCoaLoading }] = useCellOwnerAgreementsApiCreateMutation()
  const handleGenerateCOAs = async (): Promise<void> => {
    apiCreateCOA({ projectId: project.projectId })
      .unwrap()
      .then((triggerResult) => {
        const result = (triggerResult as AssetHoldingProjectIdentifier)
        dispatch(setNotification({ open: true, severity: 'success', message: `${result.numberOfCellOwnerAgreements} COAs created for project ${project.name}. The project has been refreshed.` }))
        if (triggerRefetch != null) {
          triggerRefetch()
        }
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const [apiCreateCoaPdf, { isLoading: generateCoaPdfLoading }] = useCellOwnerAgreementsApiCreatePdfMutation()
  const handleCreateCOAPdfs = async (): Promise<void> => {
    apiCreateCoaPdf({ projectId: project.projectId })
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: `COA PDF generation job for project ${project.name} has been started. Check back later to review progress.` }))
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <>
      <Grid container spacing={4}>
        <Grid item md={12} sx={{ mt: 3 }}></Grid>

        {/* ==================================================================================================== */}

        <Grid item md={4}>
          <FormikTextField formikProps={formikProps} fieldName={'cellOwnerAgreements.assetDescription'} label={'Asset Description'} type={'text'} locked={isFieldLocked()}/>
        </Grid>

        <Grid item md={4}>
          <FormikTextField formikProps={formikProps} fieldName={'cellOwnerAgreements.fixedFirstYearElectricityCost'} label={'Fixed First Year Electricity Cost'} type={'number'} locked={isFieldLocked()}/>
        </Grid>

        <Grid item md={4}>
          <FormikTextField formikProps={formikProps} fieldName={'cellOwnerAgreements.adjustmentBasisPercentage'} label={'Adjustment Basis'} type={'text'} locked={isFieldLocked()}/>
        </Grid>

        {/* ==================================================================================================== */}

        <Grid item md={12}>
          <FormikTextField formikProps={formikProps} fieldName={'cellOwnerAgreements.buyoutSchedule'} label={'Buyout Schedule'} type={'text'} locked={isFieldLocked()} multiline={true} />
        </Grid>

        {/* ==================================================================================================== */}

        <Grid item md={1}>
          <Typography variant={'body2'}>Cell owners</Typography>
          <Typography variant={'subtitle1'}>
            <strong>{project.cellOwnerAgreementsStatus.countCellOwners}</strong>
          </Typography>
        </Grid>
        <Grid item md={1}>
          <Typography variant={'body2'}>Orders</Typography>
          <Typography variant={'subtitle1'}>
            <strong>{project.cellOwnerAgreementsStatus.countOrders}</strong>
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant={'body2'}>Cell owner agreements</Typography>
          <Typography variant={'subtitle1'}>
            <strong>{project.cellOwnerAgreementsStatus.countCurrentCellOwnerAgreements}</strong>
          </Typography>
        </Grid>
        <Grid item md={2}>
          <Typography variant={'body2'}>Cell owner agreement PDFs</Typography>
          <Typography variant={'subtitle1'}>
            <strong>{project.cellOwnerAgreementsStatus.countCellOwnerAgreementPdfs}</strong>
          </Typography>
        </Grid>
        <Grid item md={2}>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel>Template Version</InputLabel>
            <Select
              {...formikProps.getFieldProps('cellOwnerAgreements.templateVersionId')}
              label={'Project Currency'}
              disabled={isFieldLocked()}
              error={formikErrors.fieldInError('cellOwnerAgreements.templateVersionId')}
            >
              {
                project.lookups.coaTemplateVersions.map((c) =>
                  <MenuItem key={c.templateVersionId} value={c.templateVersionId}>{c.version} {c.active ? '[Active]' : ''}</MenuItem>
                )
              }
            </Select>
          </FormControl>
          <FormHelperText>{formikErrors.getError('cellOwnerAgreements.templateVersionId')}</FormHelperText>
        </Grid>

        <Grid item md={4}>
          <Button variant={'outlined'}
                  type="button" onClick={handleGenerateCOAs} sx={{ mr: 2 }}
                  disabled={!project.cellOwnerAgreementsStatus.enableGenerateAgreements}>
            Generate COAs
            {createCoaLoading && <ButtonLoadingComponent/>}
          </Button>
          <Button variant={'outlined'}
                  type="button"
                  onClick={handleCreateCOAPdfs}
                  sx={{ mr: 2 }}
                  disabled={!project.cellOwnerAgreementsStatus.enableGenerateAgreementPdfs}>
            Generate COA Pdfs
            {generateCoaPdfLoading && <ButtonLoadingComponent/>}
          </Button>
        </Grid>

        {/* ==================================================================================================== */}

      </Grid>
    </>
  )
}

export default EditCellOwnerAgreementsPanel
